import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Container, Row, Col, Alert, Card, Form, Badge } from 'react-bootstrap';
import Select from 'react-select';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { ReactComponent as ArrowLeft } from '../../../img/arrow_left.svg';
import { useSensorTopics } from '../../../hooks/useSensorTopics';
import { useMqttDataLogger } from '../../../hooks/useMqttDataLogger';
import { useUnitOperations } from '../hooks/useUnitOperations';
import { ConfirmationModal } from '../../ConfirmationModal';
import { MQTT_ACTIONS } from '../unitSectionConstants';
import {
  SENSOR_COLORS,
  CHART_CONFIG,
  UI_TEXT,
  CONTROL_LABELS,
  SENSOR_GROUP_LABELS,
  STATUS_LABELS,
  SELECT_STYLES,
  createChartOptions
} from './sensorChartConstants';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

export const SensorChartPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const unitId = searchParams.get('unit');
  
  // State for selected sensors
  const [selectedSensors, setSelectedSensors] = useState([]);
  const [visibleSensors, setVisibleSensors] = useState(new Set());

  // Water flush confirmation state
  const [showWaterFlushConfirmation, setShowWaterFlushConfirmation] = useState(false);

  // Use the sensor topics hook
  const {
    groupedSensors,
    getMqttTopicsForUnit,
    getSensorDataFormat,
    error,
    sensorCount
  } = useSensorTopics(unitId);

  // Use unit operations hook for water flush functionality
  const {
    executeUnitAction,
    fetchUnits
  } = useUnitOperations(unitId);

  // Create MQTT topics array for selected sensors
  const mqttTopics = useMemo(() => {
    if (!selectedSensors.length) {
      return [];
    }
    
    const topics = [];
    selectedSensors.forEach(option => {
      const sensorTopics = getMqttTopicsForUnit(option.sensor.sensor);
      topics.push(...sensorTopics);
    });
    
    return [...new Set(topics)];
  }, [selectedSensors, getMqttTopicsForUnit]);

  // Create sensor configurations map for data processing
  const sensorConfigs = useMemo(() => {
    const configs = {};
    selectedSensors.forEach(option => {
      const sensorName = option.sensor.sensor;
      const dataFormat = getSensorDataFormat(sensorName);
      
      configs[sensorName] = {
        ...option.sensor,
        dataFormat: dataFormat
      };
    });
    return configs;
  }, [selectedSensors, getSensorDataFormat]);

  // Use MQTT data logger hook
  const {
    dataPoints,
    isConnected,
    isLogging,
    clearData,
    toggleLogging,
    dataCount
  } = useMqttDataLogger(mqttTopics, sensorConfigs);

  useEffect(() => {
    return () => {
      // Clear selected sensors to trigger topic cleanup
      setSelectedSensors([]);
    };
  }, []);

  // Initialize visible sensors when sensors are selected
  useEffect(() => {
    const newVisibleSensors = new Set();
    selectedSensors.forEach(option => {
      newVisibleSensors.add(option.sensor.sensor);
    });
    setVisibleSensors(newVisibleSensors);
  }, [selectedSensors]);

  // Set the page title
  useEffect(() => {
    document.title = `${UI_TEXT.PAGE_TITLE} - Unit ${unitId}`;
  }, [unitId]);

  // Fetch unit data on mount
  useEffect(() => {
    if (unitId) {
      fetchUnits();
    }
  }, [unitId, fetchUnits]);

  // Handle water flush confirmation
  const handleWaterFlushConfirm = async (confirmed) => {
    if (confirmed) {
      try {
        await executeUnitAction(MQTT_ACTIONS.WATER_FLUSH);
        console.log('Water flush activated for unit:', unitId);
      } catch (error) {
        console.error('Failed to activate water flush:', error);
      }
    }
    setShowWaterFlushConfirmation(false);
  };

  // Prepare chart data
  const chartData = useMemo(() => {
    const datasets = [];
    let colorIndex = 0;

    selectedSensors.forEach(option => {
      const sensorName = option.sensor.sensor;
      const sensorData = dataPoints
        .filter(point => point.sensor === sensorName)
        .sort((a, b) => a.index - b.index);

      if (sensorData.length > 0) {
        const color = SENSOR_COLORS[colorIndex % SENSOR_COLORS.length];
        datasets.push({
          label: sensorName.toUpperCase(),
          data: sensorData.map((point, index) => ({
            x: index,
            y: point.value
          })),
          borderColor: color,
          backgroundColor: color + '20',
          pointBackgroundColor: color,
          pointBorderColor: color,
          pointRadius: CHART_CONFIG.POINT_RADIUS,
          pointHoverRadius: CHART_CONFIG.HOVER_POINT_RADIUS,
          fill: false,
          tension: CHART_CONFIG.LINE_TENSION,
          hidden: !visibleSensors.has(sensorName)
        });
        colorIndex++;
      }
    });

    return { datasets };
  }, [dataPoints, selectedSensors, visibleSensors]);

  // Chart options
  const chartOptions = useMemo(() => {
    const options = createChartOptions(unitId, selectedSensors, visibleSensors, setVisibleSensors);
    
    // Disable animations
    return {
      ...options,
      animation: false
    };
  }, [unitId, selectedSensors, visibleSensors, setVisibleSensors]);

  // Go back to unit issues page
  const goBack = () => {
    navigate(`/issues?unit=${unitId}`);
  };

  // Group options for better UX
  const groupedOptions = [
    {
      label: SENSOR_GROUP_LABELS.INDIVIDUAL,
      options: groupedSensors.individual.map(sensor => ({
        value: sensor.sensor,
        label: `${sensor.sensor.toUpperCase()} (${sensor.topic})`,
        sensor: sensor
      }))
    },
    {
      label: SENSOR_GROUP_LABELS.COLOR_3BANDS,
      options: groupedSensors.color3bands.map(sensor => ({
        value: sensor.sensor,
        label: `${sensor.sensor.toUpperCase()} (${sensor.topic})`,
        sensor: sensor
      }))
    }
  ].filter(group => group.options.length > 0);

  const handleSensorChange = (selectedOptions) => {
    setSelectedSensors(selectedOptions || []);
    if (clearData) {
      clearData();
    }
  };

  if (!unitId) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <h5>Error</h5>
          <p>No unit ID provided. Please navigate from the main application.</p>
          <Button variant="primary" onClick={() => navigate('/')}>
            Go to Dashboard
          </Button>
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <h5>Configuration Error</h5>
          <p>Error loading sensor configuration: {error}</p>
          <Button variant="secondary" onClick={goBack}>
            Go Back
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <div className="mt-4">
      {/* Water Flush Confirmation Modal */}
      {showWaterFlushConfirmation && (
        <ConfirmationModal
          onShow={showWaterFlushConfirmation}
          onClose={() => setShowWaterFlushConfirmation(false)}
          onConfirm={handleWaterFlushConfirm}
          title="Are you sure you want to activate a water flush in this unit?"
          confirmButtonText="ACTIVATE WATER FLUSH"
          cancelButtonText="Cancel the operation"
        />
      )}

      {/* Header with back button */}
      <Container>
        <div className="text-start mb-3">
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-link ps-0 pe-2 mb-3"
              onClick={goBack}
            >
              <ArrowLeft style={{ width: '20px', height: '20px' }} />
            </button>
            <h5>
              <strong>{UI_TEXT.PAGE_TITLE} - Unit {unitId}</strong>
            </h5>
          </div>
        </div>

        {/* Sensor Selection */}
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">
                    <strong>Select Sensors to Monitor</strong>
                  </h6>
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg="info">{sensorCount} {STATUS_LABELS.AVAILABLE}</Badge>
                    <Badge bg={selectedSensors.length > 0 ? 'success' : 'secondary'}>
                      {selectedSensors.length} {STATUS_LABELS.SELECTED}
                    </Badge>
                    {selectedSensors.length > 0 && (
                      <Badge bg={isConnected ? 'success' : 'danger'}>
                        {isConnected ? STATUS_LABELS.CONNECTED : STATUS_LABELS.DISCONNECTED}
                      </Badge>
                    )}
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={7}>
                    <Form.Group>
                      <Form.Label>Choose Sensors:</Form.Label>
                      <Select
                        isMulti
                        value={selectedSensors}
                        onChange={handleSensorChange}
                        options={groupedOptions}
                        styles={SELECT_STYLES}
                        placeholder={UI_TEXT.SELECT_PLACEHOLDER}
                        noOptionsMessage={() => UI_TEXT.NO_OPTIONS_MESSAGE}
                        className="basic-multi-select"
                        classNamePrefix="select"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label>Data Controls:</Form.Label>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-danger"
                          onClick={() => setSelectedSensors([])}
                          size="sm"
                          disabled={selectedSensors.length === 0}
                        >
                          {CONTROL_LABELS.CLEAR_ALL}
                        </Button>
                        {selectedSensors.length > 0 && (
                          <>
                            <Button
                              variant={isLogging ? "outline-warning" : "outline-success"}
                              onClick={toggleLogging}
                              size="sm"
                            >
                              {isLogging ? CONTROL_LABELS.PAUSE : CONTROL_LABELS.RESUME}
                            </Button>
                            <Button
                              variant="outline-info"
                              onClick={clearData}
                              size="sm"
                              disabled={dataCount === 0}
                            >
                              {CONTROL_LABELS.CLEAR_DATA} ({dataCount})
                            </Button>
                          </>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Unit Actions Section */}
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header>
                <h6 className="mb-0">
                  <strong>Unit Actions</strong>
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Label className="mb-0 me-3">
                    <strong>Remote Commands:</strong>
                  </Form.Label>
                  <Button
                    variant="primary"
                    onClick={() => setShowWaterFlushConfirmation(true)}
                    size="sm"
                    title={CONTROL_LABELS.WATER_FLUSH_TOOLTIP}
                  >
                    {CONTROL_LABELS.WATER_FLUSH}
                  </Button>
                  <small className="text-muted ms-2">
                    Remotely activate water flush for unit {unitId}
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Chart Area */}
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">
                    <strong>Real-time Chart</strong>
                    {selectedSensors.length > 0 && (
                      <Badge bg="success" className="ms-2">
                        {selectedSensors.length} sensor{selectedSensors.length > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </h6>
                  {dataCount > 0 && (
                    <div className="d-flex align-items-center gap-2">
                      <Badge bg="info">{dataCount} {STATUS_LABELS.DATA_POINTS}</Badge>
                      <Badge bg={isLogging ? 'success' : 'warning'}>
                        {isLogging ? STATUS_LABELS.RECORDING : STATUS_LABELS.PAUSED}
                      </Badge>
                    </div>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                {selectedSensors.length === 0 ? (
                  <div 
                    className="d-flex align-items-center justify-content-center text-muted"
                    style={{ height: CHART_CONFIG.CHART_HEIGHT }}
                  >
                    <div className="text-center">
                      <h5 className="mb-3">{UI_TEXT.NO_SENSORS_TITLE}</h5>
                      <p>{UI_TEXT.NO_SENSORS_SUBTITLE}</p>
                      <p className="text-info">{UI_TEXT.NO_SENSORS_INFO}</p>
                      <small>Available sensors: {sensorCount}</small>
                    </div>
                  </div>
                ) : dataCount === 0 ? (
                  <div 
                    className="d-flex align-items-center justify-content-center text-muted"
                    style={{ height: CHART_CONFIG.CHART_HEIGHT }}
                  >
                    <div className="text-center">
                      <h5 className="mb-3">
                        <Badge bg={isConnected ? 'success' : 'danger'} className="me-2">
                          {isConnected ? STATUS_LABELS.CONNECTED : STATUS_LABELS.DISCONNECTED}
                        </Badge>
                        {UI_TEXT.WAITING_FOR_DATA_TITLE}
                      </h5>
                      <p>Subscribed to {mqttTopics.length} MQTT topics</p>
                      <div className="mb-3">
                        {selectedSensors.map(sensor => (
                          <Badge key={sensor.value} bg="primary" className="me-2 mb-1">
                            {sensor.sensor.sensor.toUpperCase()}
                          </Badge>
                        ))}
                      </div>
                      <small className="text-muted">
                        {isLogging ? UI_TEXT.LISTENING_MESSAGE : UI_TEXT.PAUSED_MESSAGE}
                      </small>
                    </div>
                  </div>
                ) : (
                  <div style={{ height: CHART_CONFIG.CHART_HEIGHT }}>
                    <Line data={chartData} options={chartOptions} />
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};